/**
 * Utility functions for color manipulation and contrast calculations
 */

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color string (e.g., #FFFFFF)
 * @returns {object} RGB values { r, g, b }
 */
const hexToRgb = (hex) => {
  // Remove # if present
  const cleanHex = hex.replace('#', '')
  
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)
  
  return { r, g, b }
}

/**
 * Calculate relative luminance of a color
 * Based on WCAG 2.0 formula
 * @param {object} rgb - RGB values { r, g, b }
 * @returns {number} Relative luminance (0-1)
 */
const getRelativeLuminance = (rgb) => {
  const { r, g, b } = rgb
  
  // Convert RGB values to 0-1 range
  const rsRGB = r / 255
  const gsRGB = g / 255
  const bsRGB = b / 255
  
  // Apply gamma correction
  const rLin = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4)
  const gLin = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4)
  const bLin = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4)
  
  // Calculate relative luminance
  return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin
}

/**
 * Calculate contrast ratio between two colors
 * @param {number} lum1 - Luminance of first color
 * @param {number} lum2 - Luminance of second color
 * @returns {number} Contrast ratio
 */
const getContrastRatio = (lum1, lum2) => {
  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * WCAG contrast ratio between two hex colors (1–21).
 * @param {string} hexA
 * @param {string} hexB
 * @returns {number}
 */
export const contrastRatio = (hexA, hexB) =>
  getContrastRatio(getRelativeLuminance(hexToRgb(hexA)), getRelativeLuminance(hexToRgb(hexB)))

/**
 * Hue (0–360) and saturation (0–1) of a hex color.
 * @param {string} hex
 * @returns {{ hue: number, saturation: number }}
 */
export const hueSaturation = (hex) => {
  const { r, g, b } = hexToRgb(hex)
  const max = Math.max(r, g, b) / 255
  const min = Math.min(r, g, b) / 255
  const d = max - min
  if (d === 0) return { hue: 0, saturation: 0 }
  const l = (max + min) / 2
  const saturation = d / (1 - Math.abs(2 * l - 1))
  const [rn, gn, bn] = [r / 255, g / 255, b / 255]
  let hue
  if (max === rn) hue = 60 * (((gn - bn) / d) % 6)
  else if (max === gn) hue = 60 * ((bn - rn) / d + 2)
  else hue = 60 * ((rn - gn) / d + 4)
  return { hue: (hue + 360) % 360, saturation }
}

/**
 * Determine the best contrast color (white or dark grey) for a given background color
 * @param {string} backgroundColor - Hex color string for the background
 * @returns {string} Hex color string for the text color
 */
export const getContrastColor = (backgroundColor) => {
  // Default to white if invalid color
  if (!backgroundColor || !/^#[0-9A-Fa-f]{6}$/.test(backgroundColor)) {
    return '#FFFFFF'
  }
  
  const rgb = hexToRgb(backgroundColor)
  const luminance = getRelativeLuminance(rgb)
  
  // Test contrast with white
  const whiteLuminance = 1 // White has luminance of 1
  const whiteContrast = getContrastRatio(whiteLuminance, luminance)
  
  // Test contrast with dark grey (#231F20)
  const darkGreyRgb = hexToRgb('#231F20')
  const darkGreyLuminance = getRelativeLuminance(darkGreyRgb)
  const darkGreyContrast = getContrastRatio(darkGreyLuminance, luminance)
  
  // Choose the color with better contrast
  // WCAG AA requires 4.5:1 for normal text, 3:1 for large text
  // We'll aim for at least 4.5:1
  if (whiteContrast >= 4.5 && whiteContrast >= darkGreyContrast) {
    return '#FFFFFF'
  } else if (darkGreyContrast >= 4.5) {
    return '#231F20'
  } else {
    // If neither meets the threshold, choose the one with better contrast
    return whiteContrast > darkGreyContrast ? '#FFFFFF' : '#231F20'
  }
}